import { EDischargeType, EPosition, EStatus, EUnitOfMeasure } from './enum';

export interface IProject {
    id?: number;
    isBase?: boolean;
    baseId?: number;
    projectInfo: IProjectInfo;
    drainageTrenches?: IDrainageTrench[];
    discharge?: IDischarge;
    load?: ILoad;
    phaseOneOutputs?: IPhaseOneOutput;
    slope?: ISlope;
    soilInputData?: ISoilInputData;
}

export interface IProjectInfo {
    projectId?: number;
    status?: EStatus;
    title: string;
    client: string;
    projectNumber?: number | string;
    companyName: string;
    country: string;
    projectCountryCode?: string;
    city: string;
    notes: string;
    unitOfMeasure?: EUnitOfMeasure;
    latitude?: number | string;
    longitude?: number | string;
    creationDate?: Date;
    lastUpdateDate?: Date;
    authorEmail?: string;

    //Add for business application
    authorName?: string;
    authorSurname?: string;
}

export interface IDrainageTrench extends IInputDrainageTrench {
    id?: number;
    isPrimaryDrainageTrench?: boolean;
    drainageChimney?: IDrainageChimney;
    drainPipe?: IDrainPipe;
    product?: IProduct;
}

export interface IInputDrainageTrench {
    hasDrainageChimney?: boolean;
    drainPositionDepth?: number;
    expectedWaterTableDepth?: number;
    drainLineLength?: number;
    wheelbase?: number;
    hasWaterproofMembraneBelowDrain?: boolean;
    heightOfSaturatedWaterTableBelowDrains?: number;
    drainPosition?: EPosition;
    numberOfDrains?: number;
    numberOfDrainLines?: number;
    waterTableLoweringThickness?: number;
    fillingPercentage?: number;
    roughnessCoefficient?: number;

    //Add for business application
    productType?: string;
    drainHeight?: number;
    drainUnitWidth?: number;
    drainUnitLength?: number;
    hasDrainagePiping?: boolean;
    drainSingle?: boolean;
}

export interface IDrainageCapacityProduct {
    position: EDrainageCapacityType;
    islColumns: (number | null)[];
    appliedLoadsRows: (number | null)[];
    values: (number | null)[][];
}

export enum EDrainageCapacityType {
    Vertical,
    Horizontal,
}

export interface IDrainageChimney extends IInputDrainageChimney {
    id?: number;
    unitOfMeasure?: EUnitOfMeasure;
}

export interface IInputDrainageChimney {
    configuration?: EPosition | string;
    drainageChimneyHeight?: number;
    drainageChimneyWidth?: number;
    drainageChimneyLength?: number;
    coverLayer?: number;
    chimneyWheelbase?: number;
}

export interface IDischarge extends IInputDischarge {
    id?: number;
}

export interface IInputDischarge {
    dischargeType?: EDischargeType;
    catchmentArea?: number;
    mainChannelLength?: number;
    averageCatchmentElevation?: number;
    closureSectionElevation?: number;
    returnPeriod?: number;
    parameterA?: number;
    parameterN?: number;
    flowConcentrationTime?: number;
    rainfallHeight?: number;
    curveNumber?: number;
    totalDischargeToBeDrained?: number;
}

export interface ILoad extends IInputLoad {
    id?: number;
}

export interface IInputLoad {
    distributedOverload?: number;
    hasSeismicLoads?: boolean;
    horizontalSeismicLoad?: number;
    verticalSeismicLoad?: number;
    minFsStaticConditions?: number;
    minFsSeismicConditions?: number;
    drainEfficiency?: number;
}

export interface IPhaseOneOutput {
    id?: number;
    isSlopeStable?: boolean;
    fsStaticMin?: number;
    fsSeismicMin?: number;
    phaseOneOutputFSs: IPhaseOneOutputFS[];
}

export interface IPhaseOneOutputFS {
    id?: number;
    dw?: number;
    fsStatic?: number;
    fsSeismic?: number;
}

export interface IProductPhysicalDatas extends IInputProductPhysicalDatas {
    id?: number;
    unitOfMeasure?: EUnitOfMeasure;
}

export interface IInputProductPhysicalDatas {
    drainHeight?: number;
    drainUnitWidth?: number;
    drainUnitLength?: number;
    maxUnitaryDrainageCapacity?: number;
    maximumAppliedLoad?: number;

    //Add for business application
    hasDrainagePiping?: boolean;
}

export interface IProduct {
    id?: number;
    drainageChimneyId?: number;
    isBase?: boolean;
    status?: EStatus;
    isMaccaferriManufacturer?: boolean;
    creationDate?: Date;
    lastUpdateDate?: Date;
    color?: string;
    baseId?: number;
    name?: string;
    hasPipe?: boolean;
    drainageCapacities?: IDrainageCapacityProduct[];
    images?: IImage[];
    productPhysicalDatas?: IProductPhysicalDatas[];
    translations?: ITranslation[];
}

export interface IDrainPipe {
    id?: number;
    idInApp?: string;
    isBase?: boolean;
    status?: EStatus;
    creationDate?: Date;
    lastUpdateDate?: Date;
    name?: string;
    countries?: string[];
    baseId?: number;
    drainPipeDiameters?: IDrainPipeDiameter[];
}

export interface IDrainPipeDiameter extends IInputDrainPipeDiameter {
    id?: number;
    unitOfMeasure?: EUnitOfMeasure;
}

export interface IInputDrainPipeDiameter {
    diameter?: number;
    internalDiameter?: number;
}

export interface ISoilInputData {
    id?: number;
    layersInSeries?: number | string;
    soils?: ISoil[];
}

export interface ISoil {
    id?: number;
    isBase?: boolean;
    baseId?: number;
    status?: EStatus;
    creationDate?: Date;
    lastUpdateDate?: Date;
    layerNumber?: number;
    name?: string;
    color?: string;
    soilPhysicalData?: ISoilPhysicalData[];
    images?: IImage[];
    translations?: ITranslation[];
}

export interface ISoilPhysicalData extends IInputSoil {
    id?: number;
    unitOfMeasure?: EUnitOfMeasure;
}

export interface IInputSoil {
    frictionAngle?: number;
    drainedCohesion?: number;
    undrainedCohesion?: number;
    unitWeight?: number;
    saturatedUnitWeight?: number;
    effectivePorosity?: number;
    drainedSoilPermeability?: number | string;
    permeabilityChangeUponRemodeling?: boolean;
    remodeledSoilPermeability?: number | string;
    thickness?: number;
    hydraulicConductivity?: number;

    //Add for business application
    soilType?: string;
    layerNumber?: number;
}

export interface ISlope extends IInputSlope {
    id?: number;
}

export interface IInputSlope {
    slopeWidth?: number;
    slopeLength?: number;
    slopeAngle?: number;
    transverseSlopeAngle?: number;
    initialWaterTableDepth?: number;
    impermeableLayerDepth?: number;
}

export interface IAuthor {
    id: number;
    name: string;
    surname: string;
    email: string;
    projects: IProject[];
}

export interface ICompany {
    id: number;
    name: string;
    country: string;
    city: string;
    authors: IAuthor[];
}

export interface IImage {
    id?: number;
    imageUrl?: string;
}

export interface ITranslation {
    id?: number;
    iso?: string;
    name?: string;
}

export type TInputSlope = keyof IInputSlope;
export type TInputLoad = keyof IInputLoad;
export type TInputSoil = keyof IInputSoil;
export type TInputDischarge = keyof IInputDischarge;
export type TInputDrainageTrench = keyof IInputDrainageTrench;
export type TInputDrainageChimney = keyof IInputDrainageChimney;
export type TInputDrainPipeDiameter = keyof IInputDrainPipeDiameter;
export type TSoilInputData = keyof ISoilInputData;
export type TInputProductPhysicalDatas = keyof IInputProductPhysicalDatas;
